import __var_0__ from '@stdlib/vendor/events.js';
import __var_1__ from '@stdlib/vendor/debug.js';
import __var_2__ from '@stdlib/utils/define-property';
import __var_3__ from '@stdlib/utils/define-read-only-property';
import __var_4__ from '@stdlib/utils/copy';
import __var_5__ from './defaults.json';
import __var_6__ from './validate.js';
import __var_7__ from './props/clip-path-id/set.js';
import __var_8__ from './props/clip-path-id/get.js';
import __var_9__ from './props/auto-render/set.js';
import __var_10__ from './props/auto-render/get.js';
import __var_11__ from './methods/render.js';
/**

*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //
var EventEmitter = __var_0__.EventEmitter;
var logger = __var_1__;
var defineProperty = __var_2__;
var setReadOnly = __var_3__;
var copy = __var_4__;
var defaults = __var_5__;
var validate = __var_6__;
var setClipPathId = __var_7__;
var getClipPathId = __var_8__;
var setAutoRender = __var_9__;
var getAutoRender = __var_10__;
var render = __var_11__;


// VARIABLES //

var debug = logger( 'marks:main' );


// MAIN //

/**
* Marks constructor.
*
* @constructor
* @param {Options} options - constructor options
* @param {string} [options.clipPathId] - clipping path id
* @param {boolean} [options.autoRender=false] - indicates whether to re-render on a change event
* @throws {TypeError} must provide valid options
* @returns {Marks} marks instance
*
* @example
* var marks = new Marks({
*     'clipPathId': '1234'
* });
*/
function Marks( options ) {
	var self;
	var opts;
	var err;
	if ( !( this instanceof Marks ) ) {
		return new Marks( options );
	}
	self = this;
	opts = copy( defaults );
	err = validate( opts, options );
	if ( err ) {
		throw err;
	}
	debug( 'Creating an instance with the following configuration: %s.', JSON.stringify( opts ) );
	EventEmitter.call( this );

	defineProperty( this, '_clipPathId', {
		'configurable': false,
		'enumerable': false,
		'writable': true,
		'value': opts.clipPathId
	});
	defineProperty( this, '_autoRender', {
		'configurable': false,
		'enumerable': false,
		'writable': true,
		'value': opts.autoRender
	});

	this.on( 'change', onChange );
	this.on( '_render', onRender );

	return this;

	/**
	* Callback invoked upon receiving a change event.
	*
	* @private
	*/
	function onChange() {
		debug( 'Received a change event.' );
		if ( self._autoRender ) { // eslint-disable-line no-underscore-dangle
			self.render();
		}
	}

	/**
	* Re-emits a render event.
	*
	* @private
	*/
	function onRender() {
		var args;
		var i;
		debug( 'Received a render event. Re-emitting...' );
		args = new Array( arguments.length+1 );
		args[ 0 ] = 'render';
		for ( i = 0; i < arguments.length; i++ ) {
			args[ i+1 ] = arguments[ i ];
		}
		self.emit.apply( self, args );
	}
}

/*
* Create a prototype which inherits from the parent prototype.
*/
Marks.prototype = Object.create( EventEmitter.prototype );

/*
* Set the constructor.
*/
Marks.prototype.constructor = Marks;

/**
* Clipping path id.
*
* @name clipPathId
* @memberof Marks.prototype
* @type {string}
* @throws {TypeError} must be a primitive string
*
* @example
* var marks = new Marks({
*     'clipPathId': '1234'
* });
*
* var id = marks.clipPathId;
* // returns '1234'
*/
defineProperty( Marks.prototype, 'clipPathId', {
	'configurable': false,
	'enumerable': true,
	'set': setClipPathId,
	'get': getClipPathId
});

/**
* Rendering mode. If `true`, an instance re-renders on each change event.
*
* @name autoRender
* @memberof Marks.prototype
* @type {boolean}
* @throws {TypeError} must be a boolean primitive
* @default false
*
* @example
* var marks = new Marks({
*     'autoRender': true
* });
*
* var mode = marks.autoRender;
* // returns true
*/
defineProperty( Marks.prototype, 'autoRender', {
	'configurable': false,
	'enumerable': true,
	'set': setAutoRender,
	'get': getAutoRender
});

/**
* Renders a virtual DOM tree.
*
* @name render
* @memberof Marks.prototype
* @type {Function}
* @returns {VTree} virtual tree
*
* @example
* var marks = new Marks();
*
* var out = marks.render();
*/
setReadOnly( Marks.prototype, 'render', render );


// EXPORTS //
export default Marks;
